"use client";

import {
  listAdminCourseOrgPricingTiersApiV1AdminCoursesCourseIdOrgPricingTiersGet,
  createAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersPost,
  updateAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersTierIdPatch,
  deleteAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersTierIdDelete,
} from "@/src/api/sdk.gen";
import type { CourseOrgPricingTierResponse } from "@/src/api/types.gen";
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, Users, Building, X } from "lucide-react";

interface OrgPricingTiersSectionProps {
  courseId: number;
  courseTitle: string;
  onToast: (message: string, type?: "success" | "error") => void;
}

interface TierDraft {
  name: string;
  min_seats: string;
  max_seats: string;
  price: string;
  currency: string;
  billing_interval: string;
}

const emptyDraft = (): TierDraft => ({
  name: "",
  min_seats: "1",
  max_seats: "50",
  price: "250",
  currency: "USD",
  billing_interval: "yearly",
});

export const OrgPricingTiersSection: React.FC<OrgPricingTiersSectionProps> = ({
  courseId,
  courseTitle,
  onToast,
}) => {
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<CourseOrgPricingTierResponse[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<TierDraft>(emptyDraft);
  const [editingTierId, setEditingTierId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<TierDraft>(emptyDraft);

  const fetchTiers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: reqErr } =
        await listAdminCourseOrgPricingTiersApiV1AdminCoursesCourseIdOrgPricingTiersGet({
          path: { course_id: courseId },
        });
      if (reqErr) {
        onToast("Failed to load organization pricing tiers", "error");
        return;
      }
      setTiers(data?.tiers ?? []);
    } catch (err) {
      console.error(err);
      onToast("Network error while loading organization pricing tiers", "error");
    } finally {
      setLoading(false);
    }
  }, [courseId, onToast]);

  useEffect(() => {
    void fetchTiers();
  }, [fetchTiers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createDraft.name.trim()) {
      onToast("Tier name is required", "error");
      return;
    }
    const price = parseFloat(createDraft.price);
    const minSeats = parseInt(createDraft.min_seats, 10);
    const maxSeats = createDraft.max_seats.trim() ? parseInt(createDraft.max_seats, 10) : null;

    if (isNaN(price) || price < 0) {
      onToast("Please enter a valid price", "error");
      return;
    }
    if (isNaN(minSeats) || minSeats < 1) {
      onToast("Minimum seats must be at least 1", "error");
      return;
    }
    if (maxSeats !== null && (isNaN(maxSeats) || maxSeats < minSeats)) {
      onToast("Maximum seats must be greater than or equal to minimum seats", "error");
      return;
    }

    setBusyKey("create");
    try {
      const { data, error: reqErr } =
        await createAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersPost({
          path: { course_id: courseId },
          body: {
            name: createDraft.name.trim(),
            min_seats: minSeats,
            max_seats: maxSeats,
            price,
            currency: createDraft.currency,
            billing_interval: createDraft.billing_interval,
            is_active: true,
          },
        });
      if (reqErr) {
        onToast("Failed to create tier", "error");
        return;
      }
      onToast("Organization pricing tier created successfully");
      setIsCreating(false);
      setCreateDraft(emptyDraft());
      await fetchTiers();
    } catch (err) {
      console.error(err);
      onToast("Network error while creating tier", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const handleSaveEdit = async (tierId: number) => {
    if (!editDraft.name.trim()) {
      onToast("Tier name is required", "error");
      return;
    }
    const price = parseFloat(editDraft.price);
    const minSeats = parseInt(editDraft.min_seats, 10);
    const maxSeats = editDraft.max_seats.trim() ? parseInt(editDraft.max_seats, 10) : null;

    if (isNaN(price) || price < 0) {
      onToast("Please enter a valid price", "error");
      return;
    }
    if (isNaN(minSeats) || minSeats < 1) {
      onToast("Minimum seats must be at least 1", "error");
      return;
    }
    if (maxSeats !== null && (isNaN(maxSeats) || maxSeats < minSeats)) {
      onToast("Maximum seats must be greater than or equal to minimum seats", "error");
      return;
    }

    setBusyKey(`edit:${tierId}`);
    try {
      const { error: reqErr } =
        await updateAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersTierIdPatch({
          path: { course_id: courseId, tier_id: tierId },
          body: {
            name: editDraft.name.trim(),
            min_seats: minSeats,
            max_seats: maxSeats,
            price,
            currency: editDraft.currency,
            billing_interval: editDraft.billing_interval,
          },
        });
      if (reqErr) {
        onToast("Failed to update tier", "error");
        return;
      }
      onToast("Tier updated successfully");
      setEditingTierId(null);
      await fetchTiers();
    } catch (err) {
      console.error(err);
      onToast("Network error while updating tier", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleActive = async (tier: CourseOrgPricingTierResponse) => {
    setBusyKey(`active:${tier.id}`);
    try {
      const { error: reqErr } =
        await updateAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersTierIdPatch({
          path: { course_id: courseId, tier_id: tier.id },
          body: { is_active: !tier.is_active },
        });
      if (reqErr) {
        onToast("Failed to update status", "error");
        return;
      }
      onToast(tier.is_active ? "Tier deactivated" : "Tier activated");
      await fetchTiers();
    } catch (err) {
      console.error(err);
      onToast("Network error", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (tierId: number) => {
    if (!window.confirm("Are you sure you want to delete this organization pricing tier?")) return;
    setBusyKey(`delete:${tierId}`);
    try {
      const { error: reqErr } =
        await deleteAdminCourseOrgPricingTierApiV1AdminCoursesCourseIdOrgPricingTiersTierIdDelete({
          path: { course_id: courseId, tier_id: tierId },
        });
      if (reqErr) {
        onToast("Failed to remove tier", "error");
        return;
      }
      onToast("Pricing tier removed");
      if (editingTierId === tierId) setEditingTierId(null);
      await fetchTiers();
    } catch (err) {
      console.error(err);
      onToast("Network error", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const startEdit = (tier: CourseOrgPricingTierResponse) => {
    setEditingTierId(tier.id);
    setIsCreating(false);
    setEditDraft({
      name: tier.name,
      min_seats: String(tier.min_seats),
      max_seats: tier.max_seats != null ? String(tier.max_seats) : "",
      price: String(tier.price),
      currency: tier.currency,
      billing_interval: tier.billing_interval,
    });
  };

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 bg-slate-50/60 px-6 py-4 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">B2B Organization Tiered Pricing</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Set tiered group pricing for B2B organizations based on employee seat counts (e.g. 1–50 seats, 51–100 seats).
          </p>
        </div>
        {!isCreating && (
          <button
            type="button"
            onClick={() => {
              setIsCreating(true);
              setEditingTierId(null);
              setCreateDraft({
                ...emptyDraft(),
                name: `${courseTitle} · Group Tier`,
              });
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
          >
            <Plus className="h-4 w-4" />
            Add Org Tier
          </button>
        )}
      </div>

      <div className="p-6">
        {/* Create Tier Form */}
        {isCreating && (
          <form
            onSubmit={handleCreate}
            className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50/40 p-5 shadow-inner"
          >
            <div className="flex items-center justify-between border-b border-indigo-100/80 pb-3 mb-4">
              <h3 className="text-sm font-bold text-indigo-950">New Organization Pricing Tier</h3>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700">
                  Tier Name
                  <input
                    type="text"
                    required
                    placeholder="e.g. Small Team (1–50 seats)"
                    value={createDraft.name}
                    onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Billing Interval
                  <select
                    value={createDraft.billing_interval}
                    onChange={(e) => setCreateDraft({ ...createDraft, billing_interval: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                    <option value="one_time">One-time / Lifetime</option>
                  </select>
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Min Seats
                  <input
                    type="number"
                    min="1"
                    required
                    value={createDraft.min_seats}
                    onChange={(e) => setCreateDraft({ ...createDraft, min_seats: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Max Seats (blank for unlimited)
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 50"
                    value={createDraft.max_seats}
                    onChange={(e) => setCreateDraft({ ...createDraft, max_seats: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Price
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={createDraft.price}
                      onChange={(e) => setCreateDraft({ ...createDraft, price: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>
                </div>
                <div className="w-24">
                  <label className="block text-xs font-semibold text-slate-700">
                    Currency
                    <select
                      value={createDraft.currency}
                      onChange={(e) => setCreateDraft({ ...createDraft, currency: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="USD">USD</option>
                      <option value="SAR">SAR</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2 border-t border-indigo-100/80 pt-3">
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busyKey === "create"}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 transition"
              >
                {busyKey === "create" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Create Tier
              </button>
            </div>
          </form>
        )}

        {/* Tiers List */}
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
            Loading organization pricing tiers…
          </div>
        ) : tiers.length === 0 && !isCreating ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
            <Users className="mx-auto h-8 w-8 text-slate-300" />
            <h4 className="mt-2 text-sm font-semibold text-slate-800">No organization pricing tiers configured</h4>
            <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500 leading-relaxed">
              When a course has no organization tiers, B2B workspaces can assign it freely. Add tiered pricing to charge organizations based on seat cohorts.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tiers.map((tier) => {
              const isEditing = editingTierId === tier.id;
              const isBusy = busyKey === `edit:${tier.id}` || busyKey === `active:${tier.id}` || busyKey === `delete:${tier.id}`;

              if (isEditing) {
                return (
                  <div
                    key={tier.id}
                    className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-4 shadow-xs"
                  >
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700">
                          Tier Name
                          <input
                            type="text"
                            value={editDraft.name}
                            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                          />
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700">
                          Billing Interval
                          <select
                            value={editDraft.billing_interval}
                            onChange={(e) => setEditDraft({ ...editDraft, billing_interval: e.target.value })}
                            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                          >
                            <option value="yearly">Yearly</option>
                            <option value="monthly">Monthly</option>
                            <option value="one_time">One-time / Lifetime</option>
                          </select>
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700">
                          Min Seats
                          <input
                            type="number"
                            min="1"
                            value={editDraft.min_seats}
                            onChange={(e) => setEditDraft({ ...editDraft, min_seats: e.target.value })}
                            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                          />
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700">
                          Max Seats
                          <input
                            type="number"
                            min="1"
                            value={editDraft.max_seats}
                            onChange={(e) => setEditDraft({ ...editDraft, max_seats: e.target.value })}
                            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                          />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-slate-700">
                            Price
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editDraft.price}
                              onChange={(e) => setEditDraft({ ...editDraft, price: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                            />
                          </label>
                        </div>
                        <div className="w-24">
                          <label className="block text-xs font-semibold text-slate-700">
                            Currency
                            <select
                              value={editDraft.currency}
                              onChange={(e) => setEditDraft({ ...editDraft, currency: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                            >
                              <option value="USD">USD</option>
                              <option value="SAR">SAR</option>
                            </select>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2 border-t border-indigo-100 pt-2.5">
                      <button
                        type="button"
                        onClick={() => setEditingTierId(null)}
                        className="rounded-lg px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleSaveEdit(tier.id)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Changes"}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={tier.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border p-4 transition ${
                    tier.is_active
                      ? "border-slate-200 bg-white hover:border-slate-300"
                      : "border-slate-200/70 bg-slate-50/60 opacity-60"
                  }`}
                >
                  <div className="flex items-start sm:items-center gap-3.5 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 font-bold text-sm">
                      <Users className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-semibold text-sm text-slate-900 truncate">{tier.name}</h4>
                        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                          {tier.min_seats} – {tier.max_seats ? `${tier.max_seats} seats` : "Unlimited seats"}
                        </span>
                        {!tier.is_active && (
                          <span className="rounded-md bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Interval: <span className="capitalize font-medium text-slate-700">{tier.billing_interval.replace("_", " ")}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100">
                    <div className="text-right">
                      <span className="text-base font-bold text-slate-900">
                        {tier.currency === "USD" ? "$" : `${tier.currency} `}
                        {tier.price.toFixed(2)}
                      </span>
                      <span className="text-xs text-slate-400 block sm:inline sm:ml-1">
                        / {tier.billing_interval === "one_time" ? "lifetime" : tier.billing_interval === "monthly" ? "mo" : "yr"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => void handleToggleActive(tier)}
                        disabled={isBusy}
                        title={tier.is_active ? "Deactivate tier" : "Activate tier"}
                        className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                          tier.is_active
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {tier.is_active ? "Active" : "Activate"}
                      </button>

                      <button
                        type="button"
                        onClick={() => startEdit(tier)}
                        disabled={isBusy}
                        title="Edit tier"
                        className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => void handleDelete(tier.id)}
                        disabled={isBusy}
                        title="Delete tier"
                        className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
export default OrgPricingTiersSection;
